// Reusable input objects
import * as inputs from '@/composables/builder/inputs';

// Block categories tabs
const categories = {
    content: 'Content Blocks',
    media: 'Media Blocks',
    hero: 'Hero Blocks',
    cta: 'CTA Blocks',
    dynamic: 'Dynamic Blocks',
};

export const getComponents = (api) => {
    // All options
    // https://github.com/builderio/builder/blob/ee8e6f2d/packages/core/src/builder.class.ts#L641
    // NOTE:
    // The category is used to group the blocks in the custom insert menu
    // This is a custom implementation and not part of the Builder.io API

    const agenda = {
        category: categories.content,
        component: resolveComponent('agenda'),
        name: 'Agenda (List)',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F5f437924fe3442ee99e248072a20e5f6',
        inputs: [
            ...inputs.headlineCopy, 
            {
                name: 'items',
                friendlyName: 'Items',
                type: 'list',
                subFields: [
                    {
                        name: 'headline',
                        friendlyName: 'Headline',
                        type: 'string',
                    },
                    {
                        name: 'subHead',
                        friendlyName: 'Subhead',
                        type: 'string',
                    },
                    {
                        name: 'copy',
                        friendlyName: 'copy',
                        type: 'richText',
                    }
                ],
            },
        ]
    };

    const iconGrid = {
        category: categories.content,
        component: resolveComponent('iconGrid'),
        name: 'iconGrid',
        friendlyName: 'Icon Grid',
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'cards',
                friendlyName: 'Icon Cards',
                type: 'list',
                subFields: [
                    ...inputs.headlineCopy,
                    ...inputs.image,
                    {
                        name: 'subTitle',
                        friendlyName: 'Subtitle',
                        type: 'string',
                    },
                ],
            },
        ],
    };

    const narrowContainerParent = {
        category: categories.content,
        component: resolveComponent('narrowContainerParent'),
        name: 'Narrow Container Parent',
        canHaveChildren: true,
    };

    const circleCarousel = {
        category: categories.content,
        component: resolveComponent('circleCarousel'),
        name: 'circleCarousel',
        friendlyName: 'Circle Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F63b311de7fea46b4b8f3f5c75c6aaad6',
        inputs: [
            ...inputs.headlineCopy,
            ...inputs.button,
            {
                name: 'carouselCards',
                friendlyName: 'Cards',
                type: 'list',
                subFields: [{
                        name: 'largeText',
                        friendlyName: 'Large Text',
                        type: 'string',
                    },
                    {
                        name: 'smallText',
                        friendlyName: 'Small Text',
                        type: 'string',
                    },
                ],
                defaultValue: [{
                    largeText: '100',
                    smallText: 'Small Text',
                }, ],
            },
        ],
    };

    const faq = {
        category: categories.content,
        component: resolveComponent('faq'),
        name: 'FAQ',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9f5f175dec014065a2e46fdc05cec4ba',
        inputs: [{
                name: 'headline',
                friendlyName: 'Headline',
                defaultValue: 'Headline',
                type: 'string',
            },
            {
                name: 'faqItems',
                type: 'list',
                subFields: [{
                        name: 'title',
                        friendlyName: 'Title',
                        type: 'string',
                        defaultValue: 'Title',
                    },
                    {
                        name: 'body',
                        type: 'richText',
                        friendlyName: 'Body Text',
                        default: 'Body Text',
                    },
                    {
                        name: 'video',
                        friendlyName: 'Video Embed Code',
                        type: 'string',
                    },
                ],
                defaultValue: [{
                    title: 'Title',
                    body: 'Body Text',
                }, ],
            },
            ...inputs.button,
            {
                ...inputs.button[0],
                name: 'secondButton',
                friendlyName: 'Second Button',
            },
        ],
    };

    const quoteCarousel = {
        category: categories.content,
        component: resolveComponent('quoteCarousel'),
        name: 'Quote Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F5ff5d362b4fc45be96e684c3fabb6a47',
        inputs: [
            ...inputs.headline,
            {
                name: 'quoteCards',
                friendlyName: 'Quote Cards',
                type: 'list',
                subFields: [{
                        name: 'quote',
                        friendlyName: 'Quote',
                        type: 'string',
                    },
                    {
                        name: 'attribution',
                        friendlyName: 'Attribution',
                        type: 'string',
                    },
                    {
                        name: 'attributedTitle',
                        friendlyName: 'Attributed Title',
                        type: 'string',
                    },
                ],
                defaultValue: [{
                    quote: 'Quote',
                    attribution: 'Attribution',
                    attributedTitle: 'Attributed Title',
                }, ],
            },
        ],
    };

    const imageBulletPointCarousel = {
        category: categories.content,
        component: resolveComponent('imageBulletPointCarousel'),
        name: 'Image Bullet Point Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Ff04d0c3160fa41389dc4f0c53eeefd7e',
        inputs: [
            ...inputs.headline,
            {
                name: 'bulletPoints',
                friendlyName: 'Content Cards',
                type: 'list',
                subFields: [...inputs.iconTitleContentCard],
                defaultValue: [{
                        headline: 'Headline',
                        copy: 'Copy',
                    },
                    {
                        headline: 'Headline',
                        copy: 'Copy',
                    },
                ],
            },
            {
                name: 'carouselCards',
                friendlyName: 'Images',
                type: 'list',
                subFields: [
                    ...inputs.image,
                    {
                        name: 'mobileImage',
                        friendlyName: 'Mobile image',
                        type: 'object',
                        subFields: [...inputs.image[0].subFields],
                    },
                ],
                defaultValue: [{
                    image: {
                        src: 'https://picsum.photos/650/800',
                        alt: 'Alt',
                    },
                }, ],
            },
        ],
    };

    const imageCollage = {
        category: categories.content,
        component: resolveComponent('imageCollage'),
        name: 'Image Collage',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F4d6188db12d94056906a0104e2056d63',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'images',
                friendlyName: 'Images',
                type: 'list',
                subFields: [...inputs.image],
            },
            {
                name: 'quotes',
                friendlyName: 'Testimonials',
                type: 'list',
                subFields: [
                    {
                        name: 'quote',
                        friendlyName: 'Quote',
                        type: 'string',
                    },
                    {
                        name: 'name',
                        friendlyName: 'Name',
                        type: 'string',
                    },
                    {
                        name: 'title',
                        friendlyName: 'Title',
                        type: 'string',
                    }
                ],
            }
        ],
    };

    const sponsors = {
        category: categories.content,
        component: resolveComponent('sponsors'),
        name: 'Sponsors',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9b53a657cea64dc5b53b27f4222e9cb2',
        inputs: [...inputs.headline],
    };

    const blogQuote = {
        category: categories.content,
        component: resolveComponent('blogQuote'),
        name: 'Blog Quote',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fd76eceb8a7dd4d98910e06678b92033e',
        inputs: [{
            name: 'quote',
            friendlyName: 'Quote',
            type: 'string',
        }, ],
    };

    const festivalAccordion = {
        category: categories.content,
        component: resolveComponent('festivalAccordion'),
        name: 'Festival Accordion',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fb8d8e55a18f44cf69d5c0a819a2fd4f6',
        inputs: [
            ...inputs.headlineSubhead,
            ...inputs.button,
            {
                name: 'teal',
                friendlyName: 'Use Teal Variant',
                type: 'boolean',
                defaultValue: false,
            },
            {
                name: 'accordionItems',
                friendlyName: 'Accordion Items',
                type: 'list',
                subFields: [...inputs.image, ...inputs.headlineCopy, ...inputs.anchor],
                defaultValue: [{
                    headline: 'Title',
                    content: 'Content',
                }, ],
            },
        ],
    };

    // Media Blocks

    const blogImageCarousel = {
        category: categories.media,
        component: resolveComponent('blogImageCarousel'),
        name: 'Blog Image Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fee29365c16d340019b4ab2f7dc9da187',
        inputs: [{
            name: 'carouselCards',
            friendlyName: 'Carousel Cards',
            type: 'list',
            subFields: [
                ...inputs.image,
                {
                    name: 'caption',
                    friendlyName: 'Caption',
                    type: 'string',
                },
            ],
            defaultValue: [{
                image: {
                    src: 'https://picsum.photos/800/650',
                    alt: 'alt',
                },
                caption: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit vivamus, turpis curabitur metus.',
            }, ],
        }, ],
    };

    const blogVideo = {
        category: categories.media,
        component: resolveComponent('blogVideo'),
        name: 'Blog Video Embed',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F28db6b9bb1fa4016a38614ec52fdb77f',
        inputs: [{
            name: 'videoEmbedCode',
            friendlyName: 'Video Embed Code',
            type: 'string',
            defaultValue: '<iframe loading="lazy" width="560" height="315" src="https://www.youtube.com/embed/J7dDoY1qXB8?si=4aY_YNOJY93Gfzvp" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>',
        }, ],
    };

    // Hero Blocks
    const hero = {
        category: categories.hero,
        component: resolveComponent('hero'),
        name: 'Homepage Hero',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fffb44c5a272140ef8ad82b3a51a9b511',
        inputs: [
            ...inputs.headline,
            {
                name: 'dates',
                type: 'object',
                subFields: [{
                        name: 'start',
                        type: 'date',
                    },
                    {
                        name: 'end',
                        type: 'date',
                    },
                ],
            },
            ...inputs.video,
            {
                name: 'youtubeID',
                friendlyName: 'Youtube ID',
                type: 'string',
                defaultValue: 'J7dDoY1qXB8',
            },
            {
                name: 'location',
                type: 'string',
            },
            {
                name: 'venue',
                type: 'string',
            },
            {
                name: 'copy',
                type: 'richText',
            },
            ...inputs.button,
        ],
    };

    const heroSponsors = {
        category: categories.hero,
        component: resolveComponent('heroSponsors'),
        name: 'Interior hero',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F12f31683fd1f4d4ca336274bfc2a66b3',
        inputs: [
            ...inputs.headlineCopy,
            ...inputs.button,
            ...inputs.image,
            {
                name: 'videos',
                friendlyName: 'Videos',
                type: 'object',
                helperText: 'If an image is selected for the "image" field, the video field will have no effect.',
                subFields: [
                    ...inputs.video,
                    {
                        name: 'short',
                        friendlyName: 'Short Video Version Id',
                        type: 'string',
                    },
                    {
                        name: 'full',
                        friendlyName: 'Full Video Version Id',
                        type: 'string',
                    },
                ],
                defaultValue: {
                    short: '7httGup7qhU',
                    full: 'UjC4iS0nxps',
                },
            },
        ],
    };

    const manualEntryThreeUp = {
        category: categories.cta,
        name: 'Manual Entry Three Up',
        component: resolveComponent('manualEntryThreeUp'),
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'hideTopDecorations',
                friendlyName: 'Hide Top Background Graphics',
                type: 'boolean',
                defaultValue: false,
                helperText: 'This will hide the background graphics on the top of the component and reduce its padding.',
            },
            {
                name: 'hideBottomDecorations',
                friendlyName: 'Hide Bottom Background Graphics',
                type: 'boolean',
                defaultValue: false,
                helperText: 'This will hide the background graphics on the top of the component and reduce its padding.',
            },            
            {
                name: 'cards',
                friendlyName: 'Cards',
                type: 'list',
                subFields: [...inputs.image, ...inputs.headlineCopy, ...inputs.button],
            },
        ],
    };

    const narrowImageThreeUp = {
        category: categories.cta,
        name: 'Narrow Image Three Up',
        component: resolveComponent('narrowImageThreeUp'),
        inputs: [{
            name: 'cards',
            friendlyName: 'Cards',
            type: 'list',
            subFields: [...inputs.headlineCopy, ...inputs.image, ...inputs.button],
            defaultValue: [{
                    headline: 'Headline',
                    copy: 'Copy',
                    button: {
                        label: 'Button Label',
                        title: 'Accessibility',
                        url: 'https://inbound.com',
                    },
                },
                {
                    headline: 'Headline',
                    copy: 'Copy',
                    button: {
                        label: 'Button Label',
                        title: 'Accessibility',
                        url: 'https://inbound.com',
                    },
                },
                {
                    headline: 'Headline',
                    copy: 'Copy',
                    button: {
                        label: 'Button Label',
                        title: 'Accessibility',
                        url: 'https://inbound.com',
                    },
                },
            ],
        }, ],
    };

    const imageContentAccordion = {
        category: categories.cta,
        name: 'Image Content Accordion',
        component: resolveComponent('imageContentAccordion'),
        inputs: [
            ...inputs.headlineCopy,
            ...inputs.button,
            ...inputs.image,
            {
                name: 'accordionItems',
                friendlyName: 'Accordion Items',
                type: 'list',
                subFields: [{
                        name: 'title',
                        friendlyName: 'Title',
                        type: 'string',
                    },
                    {
                        name: 'body',
                        friendlyName: 'Copy',
                        type: 'richText',
                    },
                ],
            },
        ],
    };

    const imageTextCta = {
        category: categories.cta,
        name: 'Image Text CTA',
        component: resolveComponent('imageTextCta'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F580e428a1f4e45e7a81bbd84be728b33',
        inputs: [...inputs.headlineCopy, ...inputs.image, ...inputs.button],
    };

    const imageTextSplit = {
        category: categories.cta,
        name: 'Image Text Split',
        component: resolveComponent('imageTextSplit'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fca928e95645d4e60a412d112ca51ad33',
        inputs: [...inputs.headlineCopy, ...inputs.image, ...inputs.button, ...inputs.backgroundColor],
    };

    // CTA Blocks

    const manualEntryCarousel = {
        category: categories.cta,
        name: 'Manual Entry Carousel',
        component: resolveComponent('manualEntryCarousel'),
        inputs: [
            ...inputs.headline,
            {
                name: 'cards',
                friendlyName: 'Cards',
                type: 'list',
                subFields: [
                    ...inputs.headlineCopy,
                    {
                        name: 'subHead',
                        friendlyName: 'Subhead',
                        type: 'string',
                    },
                    ...inputs.image,
                    ...inputs.button,
                ],
            },
            {
                name: 'cardBleed',
                friendlyName: 'Card Bleed',
                type: 'boolean',
                defaultValue: true,
            },
        ],
    };

    const imageTextTwoUp = {
        category: categories.cta,
        name: 'Image Text Two Up',
        component: resolveComponent('imageTextTwoUp'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fd1dd7a68f89c4140aad0b6e58d72a4f1',
        inputs: [{
            name: 'cards',
            type: 'object',
            friendlyName: 'Cards',
            subFields: [{
                    ...inputs.twoUpCard,
                    name: 'first_card',
                    friendlyName: 'First Card',
                },
                {
                    ...inputs.twoUpCard,
                    name: 'second_card',
                    friendlyName: 'Second Card',
                },
            ],
            defaultValue: {
                first_card: {
                    headline: 'Headline',
                    copy: '<p>Copy</p>',
                    image: {
                        src: 'https://picsum.photos/500/800',
                        alt: 'Alt',
                    },
                    button: {
                        label: 'Button Label',
                        title: 'Button Title',
                        url: 'https://inbound.com',
                    },
                },
                second_card: {
                    headline: 'Headline',
                    copy: '<p>Copy</p>',
                    image: {
                        src: 'https://picsum.photos/500/800',
                        alt: 'Alt Text',
                    },
                    button: {
                        label: 'Button Label',
                        title: 'Button title',
                        url: 'https://inbound.com',
                    },
                },
            },
        }, ],
    };

    const iconList = {
        category: categories.cta,
        name: 'Icon List',
        component: resolveComponent('iconList'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F7b440fbefd314747bf013e5100383fb1',
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'bulletPoints',
                friendlyName: 'Content Cards',
                type: 'list',
                subFields: [...inputs.iconTitleContentCard],
                defaultValue: [{
                        headline: 'Headline',
                        copy: 'Copy',
                    },
                    {
                        headline: 'Headline',
                        copy: 'Copy',
                    },
                ],
            },
            {
                name: 'secondColumn',
                friendlyName: 'Add second column?',
                type: 'boolean',
                defaultValue: false,
            },
        ],
    };

    const iconTitleContent = {
        category: categories.cta,
        name: 'Icon Title Content',
        component: resolveComponent('iconTitleContent'),
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F31a9fd4a73dc4a15b2b642bd0b176534',
        inputs: [
            ...inputs.headline,
            ...inputs.image,
            {
                name: 'iconTitleContentCards',
                friendlyName: 'Cards',
                type: 'list',
                subFields: [...inputs.iconTitleContentCard],
                defaultValue: [{
                    headline: 'Headline',
                    copy: 'Copy',
                }, ],
            },
            {
                name: 'bottomText',
                friendlyName: 'Bottom Copy',
                type: 'richText',
                defaultValue: 'Lorem ipsum dolor sit amet consectetur adipiscing elit massa, penatibus scelerisque libero ridiculus leo platea fusce.',
            },
            ...inputs.button,
            {
                name: 'downloadLink',
                friendlyName: 'Download Link',
                type: 'object',
                subFields: [...inputs.button],
                defaultValue: {
                    href: 'https://inbound.com',
                    text: 'Download Link',
                },
            }
        ],
    };

    const imageContentCarousel = {
        category: categories.cta,
        component: resolveComponent('imageContentCarousel'),
        name: 'Image Content Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F42d02426abc04bb6902b10528b6d05e2',
        inputs: [
            ...inputs.headline,
            {
                name: 'carouselCards',
                friendlyName: 'Carousel Cards',
                type: 'list',
                subFields: [
                    ...inputs.image,
                    ...inputs.headlineCopy,
                    ...inputs.button,
                    {
                        name: 'eyebrowText',
                        friendlyName: 'Eyebrow Text',
                        type: 'string',
                    },
                ],
            },
        ],
    };

    const decoratedSplitCTA = {
        category: categories.cta,
        component: resolveComponent('decoratedSplitCta'),
        name: 'Decorated Split CTA',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F2ab3120387a141d7b2d3ef138f9a3af4',
        inputs: [...inputs.headlineCopy, ...inputs.button, ...inputs.image],
    };

    const splitImageTextCTA = {
        category: categories.cta,
        component: resolveComponent('splitImageTextCta'),
        name: 'Split Image Text CTA',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fa12c8cc264584547803d8b8b5d2a3747',
        inputs: [
            ...inputs.headlineCopy,
            ...inputs.button,
            ...inputs.image,
            {
                name: 'appVariant',
                friendlyName: 'App Buttons Variant',
                type: 'boolean',
            },
        ],
    };

    const blogRegistrationCTA = {
        category: categories.cta,
        component: resolveComponent('blogRegistrationCta'),
        name: 'Blog Registration CTA',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F79aa4b695e6a46528ef641cb67c7f674',
        inputs: [...inputs.headlineCopy, ...inputs.button, ...inputs.image],
    };

    const imageTextCountdown = {
        category: categories.cta,
        component: resolveComponent('imageTextCountdown'),
        name: 'Image Text Countdown',
        // screenshot:
        //   'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fa9ce8699751140bc825a4258488ef7c7',
        inputs: [
            ...inputs.headlineCopy,
            ...inputs.button,
            {
                ...inputs.button[0],
                name: 'secondButton',
                friendlyName: 'Second Button',
            },
            ...inputs.image,
            {
                name: 'subText',
                friendlyName: 'Subtext',
                type: 'string',
                defaultValue: 'Lorum',
            },
            {
                name: 'countdownDate',
                friendlyName: 'Countdown Date',
                type: 'date',
                defaultValue: 'September 17, 2024 03:24:00',
            },
            {
                name: 'straightBackground',
                friendlyName: 'Straight Background Version',
                type: 'boolean',
            },
        ],
    };

    const newsletterSignup = {
        category: categories.cta,
        component: resolveComponent('newsletterSignup'),
        name: 'Newsletter Signup.',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Ffa3833db05224f8bbe73b6a668c357e6',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'subText',
                friendlyName: 'Subtext',
                type: 'richText',
            },
            {
                name: 'buttonText',
                friendlyName: 'Button Text',
                type: 'string',
            },
            {
                name: 'container',
                type: 'boolean',
                friendlyName: 'Enable Container',
            },
        ],
    };

    const headlineCopyCTA = {
        category: categories.cta,
        component: resolveComponent('headlineCopyCta'),
        name: 'Headline Copy CTA',
        inputs: [...inputs.headlineCopy, ...inputs.button],
    };

    const fullWidthImageTextTwoUp = {
        category: categories.cta,
        component: resolveComponent('fullWidthImageTextTwoUp'),
        name: 'Full Width Image Text Two Up',
        inputs: [{
                name: 'cardOne',
                friendlyName: 'First Card',
                type: 'object',
                subFields: [
                    ...inputs.headlineCopy,
                    ...inputs.image,
                    ...inputs.button,
                    {
                        name: 'displayAppLinks',
                        friendlyName: 'Display App Links',
                        type: 'boolean',
                    },
                ],
                defaultValue: {
                    name: 'Full Width Image Text Two Up',
                    options: {
                        cardOne: {
                            headline: 'Headline',
                            copy: '<p>Lorem ipsum dolor sit amet consectetur adipiscing, elit justo nulla nec vel consequat, aliquet erat sem tristique sodales.</p>',
                        },
                        cardTwo: {
                            headline: 'Headline',
                            copy: '<p>Lorem ipsum dolor sit amet consectetur adipiscing, elit justo nulla nec vel consequat, aliquet erat sem tristique sodales.</p>',
                        },
                    },
                },
            },
            {
                name: 'cardTwo',
                friendlyName: 'Second Card',
                type: 'object',
                subFields: [
                    ...inputs.headlineCopy,
                    ...inputs.image,
                    ...inputs.button,
                    {
                        name: 'displayAppLinks',
                        friendlyName: 'Display App Links',
                        type: 'boolean',
                    },
                ],
                defaultValue: {
                    name: 'Full Width Image Text Two Up',
                    options: {
                        cardOne: {
                            headline: 'Headline',
                            copy: '<p>Lorem ipsum dolor sit amet consectetur adipiscing, elit justo nulla nec vel consequat, aliquet erat sem tristique sodales.</p>',
                        },
                        cardTwo: {
                            headline: 'Headline',
                            copy: '<p>Lorem ipsum dolor sit amet consectetur adipiscing, elit justo nulla nec vel consequat, aliquet erat sem tristique sodales.</p>',
                        },
                    },
                },
            },
            {
                name: 'decorated',
                friendlyName: 'Decorated',
                type: 'boolean',
            },
            ...inputs.backgroundColor,
        ],
    };

    const videoExpanding = {
        category: categories.cta,
        component: resolveComponent('videoExpanding'),
        name: 'Expanding Video CTA',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F36481b30d987436998ccb06535f6fdb7',
        inputs: [{
                name: 'slides',
                friendlyName: 'Slides',
                type: 'list',
                subFields: [...inputs.headlineCopy, ...inputs.button],
            },
            ...inputs.video,
            {
                name: 'videoID',
                friendlyName: 'Video Id',
                type: 'string',
                defaultValue: '-ObCO3i_Tl0',
            },
        ],
    };

    const iframe = {
        category: categories.dynamic,
        component: resolveComponent('iframe'),
        name: 'iframe',
        inputs: [{
                name: 'iFrameOne',
                friendlyName: 'iFrame One',
                type: 'string',
            },
            {
                name: 'iFrameTwo',
                friendlyName: 'iFrame Two',
                type: 'string',
            },
        ],
    };

    const blockStickyNavContainer = {
        category: categories.dynamic,
        name: 'Block Sticky Nav Container',
        component: resolveComponent('blockStickyNavContainer'),
        canHaveChildren: true,
        inputs: [{
            name: 'childComponentIds',
            friendlyName: 'Child Component Ids',
            type: 'list',
            subFields: [{
                    name: 'label',
                    friendlyName: 'Label',
                    type: 'string',
                    helperText: 'The label for the sticky nav item, if left blank the label will be generated from the ID',
                },
                {
                    name: 'childId',
                    friendlyName: 'Child Id',
                    type: 'string',
                },
            ],
        }, ],
    };

    const sessionSpeakerCarousel = {
        category: categories.dynamic,
        component: resolveComponent('sessionSpeakerCarousel'),
        name: 'Session Speaker Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fbea17d75a7de4409acca3ef9051dc603',
        inputs: [
            ...inputs.headline,
            {
                name: 'sessionId',
                friendlyName: 'Session ID',
                type: 'string',
                helperText: 'The session ID to pull speakers from. NOTE: If this block is used on a session page, the session ID will be automatically pulled from the page.',
            },
        ],
    };

    const sessionLiveStreamCarousel = {
        category: categories.dynamic,
        component: resolveComponent('sessionsLiveStreamCarousel'),
        name: 'Session Live Stream Carousel',
        inputs: [...inputs.headlineCopy, ...inputs.button],
    };

    const bizzaboWidget = {
        category: categories.dynamic,
        component: resolveComponent('RegistrationForm'),
        name: 'Registration Form',
        inputs: [...inputs.headline],
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fe93790df1da6435ebb654f28a66a2a15',
    };

    const registration = {
        category: categories.dynamic,
        component: resolveComponent('registration'),
        name: 'Registration',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Ff45b80d2a8a244f8ac2663fcf08ac55d',
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'date',
                friendlyName: 'Date Text',
                type: 'string',
                helperText: 'Displays to the right of the headline',
            },
            {
                name: 'location',
                friendlyName: 'Location Text',
                type: 'string',
                helperText: 'Displays to the right of the headline',
            },
            {
                name: 'showDetails',
                friendlyName: 'Show pass details',
                type: 'boolean',
            },
            {
                name: 'teal',
                friendlyName: 'Use teal background',
                helperText: 'Switch this block to use its teal background variant',
                type: 'boolean',
            },
            {
                name: 'bizzabo',
                friendlyName: 'Show Registration Widget',
                helperText: 'Display the Bizzabo registration widget below panels',
                type: 'boolean',
            },
            {
                name: 'links',
                friendlyName: 'Link Blocks',
                helperText: 'Displays beneath registration as large block',
                type: 'list',
                subFields: [
                    {
                        name: 'label',
                        friendlyName: 'Label',
                        type: 'string'
                    },
                    {
                        name: 'copy',
                        friendlyName: 'Description',
                        type: 'string'
                    },
                    {
                        name: 'url',
                        friendlyName: 'URL',
                        type: 'url'
                    },
                    {
                        name: 'buttonLabel',
                        friendlyName: 'Button Label',
                        type: 'string'
                    }
                ],
            }
        ],
    };

    const registrationSingle = {
        category: categories.dynamic,
        component: resolveComponent('registrationSingle'),
        name: 'Registration Single',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Ff45b80d2a8a244f8ac2663fcf08ac55d',
        inputs: [
            ...inputs.headlineCopy,
            {
                name: 'showDetails',
                friendlyName: 'Show pass details',
                type: 'boolean',
            },
        ],
    };

    const communitiesGrid = {
        category: categories.dynamic,
        component: resolveComponent('communitiesGrid'),
        name: 'Communities Grid',
        inputs: [],
    };

    const hubspotFormEmbed = {
        category: categories.dynamic,
        component: resolveComponent('hubspotFormEmbed'),
        name: 'Hubspot Form Embed',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F29de0ec787cd4b0ca764bbbaf5170db8',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'anchorId',
                friendlyName: 'Anchor Id',
                type: 'string',
            },
            {
                name: 'embedScript',
                friendlyName: 'Hubspot Form Embed Text',
                helperText: 'Paste the code supplied from hubspot here. This block will handle it from there!',
                type: 'string',
                defaultValue: '<script charset="utf-8" type="text/javascript" src="//js.hsforms.net/forms/embed/v2.js"></script> <script>   hbspt.forms.create({     region: "na1",     portalId: "146726",     formId: "efc5c433-5b6d-423a-aaab-f2fb40604f6c"   }); </script>',
            },
            {
                name: 'forceWhite',
                friendlyName: 'Use White Background',
                type: 'boolean',
            },
        ],
    };

    const soldOutForm = {
        category: categories.dynamic,
        component: resolveComponent('soldOutForm'),
        name: 'Sold Out Form',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F29de0ec787cd4b0ca764bbbaf5170db8',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'anchorId',
                friendlyName: 'Anchor Id',
                type: 'string',
            },
            {
                name: 'embedScript',
                friendlyName: 'Hubspot Form Embed Text',
                helperText: 'Paste the code supplied from hubspot here. This block will handle it from there!',
                type: 'string',
                defaultValue: '<script charset="utf-8" type="text/javascript" src="//js.hsforms.net/forms/embed/v2.js"></script> <script>   hbspt.forms.create({     region: "na1",     portalId: "146726",     formId: "efc5c433-5b6d-423a-aaab-f2fb40604f6c"   }); </script>',
            },
            {
                name: 'bottomText',
                friendlyName: 'Bottom Text',
                type: 'richText',
            },
        ],
    };

    const blogHero = {
        category: categories.dynamic,
        component: resolveComponent('blogHero'),
        name: 'Blog Hero',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fb51138cf449f44439cabca00009e8aaf',
        inputs: [
            ...inputs.headlineSubhead,
            {
                name: 'includeNewsletter',
                friendlyName: 'Include Newsletter',
                type: 'boolean',
            },
        ],
    };

    const blogThreeUp = {
        category: categories.dynamic,
        component: resolveComponent('blogThreeUp'),
        name: 'Blog Three Up',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F78a68c13c9944defb9b4e60bbc821ae1',
        inputs: [
            ...inputs.headline,
            ...inputs.button,
            {
                name: 'backgroundColor',
                friendlyName: 'Background Color',
                type: 'color',
                defaultValue: '#F8F5EE',
            },
            {
                name: 'blog_posts',
                friendlyName: 'Blog Posts',
                type: 'list',
                subFields: [{
                    name: 'post',
                    friendlyName: 'Post',
                    type: 'reference',
                    model: 'blog',
                }, ],
            },
        ],
    };

    const blogFilterGrid = {
        category: categories.dynamic,
        component: resolveComponent('blogFilterGrid'),
        name: 'Blog Filter Grid',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fe164f127ff9d422db251aada2173353e',
        inputs: [{
                name: 'limit',
                friendlyName: 'Limit Per Page',
                type: 'number',
                defaultValue: 12,
            },
            {
                name: 'expandFiltersByDefault',
                friendlyName: 'Expand Filters By Default',
                type: 'boolean',
            },
        ],
    };

    const sessionsFeaturedCarousel = {
        category: categories.dynamic,
        component: resolveComponent('sessionsFeaturedCarousel'),
        name: 'Sessions Featured Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F32feb4af077e4ae3bed8e3f410ad3dd1',
        inputs: [
            ...inputs.headlineSubheadCopy,
            {
                name: 'treatAsHero',
                friendlyName: 'Treat As Hero',
                type: 'boolean',
            },
            ...inputs.button,
        ],
    };

    const sessionsFilterGrid = {
        category: categories.dynamic,
        component: resolveComponent('sessionsFilterGrid'),
        name: 'Sessions Filter Grid',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9c3306743a9b4ffaab65b4ce1fe42907',
        inputs: [{
                name: 'limit',
                friendlyName: 'Limit Per Page',
                type: 'number',
                defaultValue: 12,
            },
            {
                name: 'expandFiltersByDefault',
                friendlyName: 'Expand Filters By Default',
                type: 'boolean',
            },
        ],
    };

    const sessionsSchedule = {
        category: categories.dynamic,
        component: resolveComponent('sessionsSchedule'),
        name: 'Sessions - Full Schedule',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F9c3306743a9b4ffaab65b4ce1fe42907',
        inputs: [
            ...inputs.headlineSubheadCopy,
            {
                name: 'limit',
                friendlyName: 'Limit Per Page',
                type: 'number',
                defaultValue: 12,
            },
            {
                name: 'enableFavorites',
                friendlyName: 'Enable Favorites',
                type: 'boolean',
            },
            {
                name: 'enableCalendar',
                friendlyName: 'Show Calendar Functionality',
                type: 'boolean',
            },
            {
                name: 'enableSeats',
                friendlyName: 'Enabled Saved Seats',
                type: 'boolean',
            },
            {
                name: 'enableAssistant',
                friendlyName: 'Enable Assistant',
                type: 'boolean',
            },            
        ],
    };

    const sessionsTargetedAgendaCarousel = {
        category: categories.dynamic,
        component: resolveComponent('sessionsTargetedAgendaCarousel'),
        name: 'Targeted Agenda Carousel',
        inputs: [...inputs.headlineCopy, ...inputs.button],
    };

    const speakersFeaturedCarousel = {
        category: categories.dynamic,
        component: resolveComponent('speakersFeaturedCarousel'),
        name: 'Speakers Featured Carousel',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fab9902189f024d368caeaf060910c7e7',
        inputs: [            
            {
                name: 'treatAsHero',
                friendlyName: 'Treat As Hero',
                type: 'boolean',
            },
            ...inputs.headlineSubheadCopy,
            ...inputs.button,
            {
                name: 'eyebrowText',
                friendlyName: 'Eyebrow Text',
                type: 'string',
            },
            {
                name: 'speakers',
                friendlyName: 'Speakers',
                type: 'list',
                subFields: [
                    {
                        name: 'name',
                        friendlyName: 'Speaker Name',
                        type: 'string'
                    },
                    {
                        name: 'title',
                        friendlyName: 'Speaker Title',
                        type: 'string'
                    },
                    {
                        name: 'bio',
                        friendlyName: 'Speaker Bio',
                        type: 'string'
                    },
                    ...inputs.image,
                ],
            },
            {
                name: 'lazyLoad',
                friendlyName: 'Lazy Load',
                type: 'boolean',
                defaultValue: true,
            },
        ],
    };

    const speakersFilterGrid = {
        category: categories.dynamic,
        component: resolveComponent('speakersFilterGrid'),
        name: 'Speakers Filter Grid',
        screenshot: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fd8fc805f25334555b4e474fe1fec9ba1',
        inputs: [{
                name: 'limit',
                friendlyName: 'Limit Per Page',
                type: 'number',
                defaultValue: 12,
            },
            {
                name: 'expandFiltersByDefault',
                friendlyName: 'Expand Filters By Default',
                type: 'boolean',
            },
        ],
    };

    const stages = {
        category: categories.dynamic,
        component: resolveComponent('stages'),
        name: 'Stages',
        inputs: [
            ...inputs.headlineCopy, 
            {
                name: 'stages',
                friendlyName: 'Stages',
                type: 'list',
                subFields: [
                    {
                        name: 'headline',
                        friendlyName: 'Stage Name',
                        type: 'string'
                    },
                    {
                        name: 'copy',
                        friendlyName: 'Copy',
                        type: 'string'
                    },
                    {
                        name: 'new',
                        friendlyName: 'Is new this year?',
                        type: 'boolean',
                    },
                    ...inputs.image,
                ],
            },
        ],
    };

    const googleMap = {
        category: categories.dynamic,
        component: resolveComponent('googleMap'),
        name: 'Google Map',
        inputs: [
            ...inputs.headlineSubheadCopy,
            ...inputs.button,
            ...inputs.location,
            ...inputs.image,
            {
                name: 'hideMap',
                friendlyName: 'Hide Map',
                helperText: 'Will hide the embedded Google Map, preferring the image. Otherwise, the image will display until the map loads.',
                type: 'boolean',
            }
        ],
    };

    const blocks = [
        agenda,
        bizzaboWidget,
        blockStickyNavContainer,
        blogFilterGrid,
        blogHero,
        blogImageCarousel,
        blogQuote,
        blogRegistrationCTA,
        blogThreeUp,
        blogVideo,
        circleCarousel,
        communitiesGrid,
        decoratedSplitCTA,
        faq,
        festivalAccordion,
        fullWidthImageTextTwoUp,
        headlineCopyCTA,
        hero,
        heroSponsors,
        hubspotFormEmbed,
        iconGrid,
        iconList,
        iconTitleContent,
        iframe,
        imageBulletPointCarousel,
        imageCollage,
        imageContentAccordion,
        imageContentCarousel,
        imageTextCountdown,
        imageTextCta,
        imageTextSplit,
        imageTextTwoUp,
        manualEntryCarousel,
        manualEntryThreeUp,
        narrowContainerParent,
        narrowImageThreeUp,
        newsletterSignup,
        quoteCarousel,
        registration,
        registrationSingle,
        sessionLiveStreamCarousel,
        sessionSpeakerCarousel,
        sessionsFeaturedCarousel,
        sessionsFilterGrid,
        sessionsSchedule,
        sessionsTargetedAgendaCarousel,
        soldOutForm,
        speakersFeaturedCarousel,
        speakersFilterGrid,
        splitImageTextCTA,
        sponsors,
        videoExpanding,
        stages,
        googleMap
    ];

    return blocks;
};

//const setCategories = (api, blocks) => {
//    // if (process.client) return;
//
//    builder.init(api);
//
//    // https:github.com/BuilderIO/builder/blob/4f0dc79c1904a4133de6421056a02db8a493bed1/plugins/example-app-simple/src/plugin.ts#L5
//    Builder.register('editor.settings', {
//        customInsertMenu: true,
//        hideFormComponents: true,
//        hideDataTab: true,
//        // hideSymbols: true,
//        // hideTemplates: true,
//        hideAnimateTab: true,
//        hideVisualEditor: true,
//        hideImportTab: true,
//    });
//
//    Object.values(categories).forEach((category) => {
//        Builder.register('insertMenu', {
//            name: category,
//            items: blocks
//                .filter((block) => block.category === category)
//                .map((block) => {
//                    return {
//                        name: block.name,
//                    };
//                }),
//        });
//    });
//};
